import React from 'react';

interface KPIDashboardSkeletonProps {
  currentViewType?: 'desktop' | 'mobile';
}

export function KPIDashboardSkeleton({ currentViewType = 'desktop' }: KPIDashboardSkeletonProps) {
  const isMobile = currentViewType === 'mobile';
  
  return (
    <div className={`min-h-screen ${isMobile ? 'bg-gray-50' : 'bg-gray-100'}`} style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' 
    }}>
      {/* Header Skeleton */}
      <div className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-8 bg-gray-200 rounded-lg w-48 animate-pulse"></div>
              <div className="h-6 bg-gray-100 rounded-full w-20 animate-pulse"></div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="h-10 bg-gray-200 rounded-xl w-32 animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded-xl w-40 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Navigation Skeleton */}
      <div className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center space-x-6">
            <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
            <div className="h-6 bg-gray-100 rounded w-32 animate-pulse"></div>
            <div className="h-6 bg-gray-100 rounded w-28 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Main Content Skeleton */}
      <div className="p-4 max-w-7xl mx-auto">
        {/* Controls Bar Skeleton */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="h-10 bg-gray-200 rounded-xl w-40 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded-xl w-32 animate-pulse"></div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="h-10 bg-gray-200 rounded-xl w-24 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded-xl w-36 animate-pulse"></div>
          </div>
        </div>

        {/* Grid Layout Skeleton */}
        <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-4'}`}>
          {/* KPI Cards Skeleton */}
          {Array.from({ length: isMobile ? 4 : 8 }).map((_, index) => (
            <KPICardSkeleton key={index} isMobile={isMobile} />
          ))}
          
          {/* Chart Widgets Skeleton */}
          {Array.from({ length: isMobile ? 2 : 4 }).map((_, index) => (
            <ChartWidgetSkeleton key={`chart-${index}`} isMobile={isMobile} />
          ))}
        </div>
      </div>
    </div>
  );
}

function KPICardSkeleton({ isMobile }: { isMobile: boolean }) {
  return (
    <div className={`bg-white rounded-2xl p-6 shadow-sm border border-gray-200/50 animate-pulse ${isMobile ? 'col-span-1' : 'col-span-1'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="h-4 bg-gray-200 rounded w-20"></div>
        <div className="h-6 w-6 bg-gray-200 rounded"></div>
      </div>
      
      {/* Main Value */}
      <div className="h-8 bg-gray-200 rounded w-24 mb-2"></div>
      
      {/* Comparison */}
      <div className="flex items-center space-x-2">
        <div className="h-4 w-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-100 rounded w-16"></div>
      </div>
    </div>
  );
}

function ChartWidgetSkeleton({ isMobile }: { isMobile: boolean }) {
  return (
    <div className={`bg-white rounded-2xl p-6 shadow-sm border border-gray-200/50 animate-pulse ${isMobile ? 'col-span-1' : 'col-span-2'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="h-5 bg-gray-200 rounded w-32"></div>
        <div className="h-6 w-6 bg-gray-200 rounded"></div>
      </div>
      
      {/* Chart Area */}
      <div className="space-y-3">
        {/* Chart bars/lines skeleton */}
        <div className="flex items-end space-x-2 h-32">
          {Array.from({ length: 7 }).map((_, i) => (
            <div 
              key={i} 
              className="bg-gray-200 rounded-t w-full animate-pulse"
              style={{ height: `${Math.random() * 60 + 40}%` }}
            ></div>
          ))}
        </div>
        
        {/* X-axis labels */}
        <div className="flex justify-between">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="h-3 bg-gray-100 rounded w-8"></div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default KPIDashboardSkeleton;
